import {
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type { GPUInstanceType } from "types/InstanceTypes";
import {
  getInstanceTypeForCloudCredential,
  getPublicInstanceTypes,
} from "./api";

const instanceTypeStaleTimeMs = 5 * 60 * 1000;

export const instanceTypeKeys = {
  all: ["devplane", "instance-types"] as const,
  publicList: () => [...instanceTypeKeys.all, "public"] as const,
  detail: (instanceType: string, cloudCredentialId: string) =>
    [
      ...instanceTypeKeys.all,
      "detail",
      instanceType,
      cloudCredentialId,
    ] as const,
};

export function publicInstanceTypesQueryOptions() {
  return {
    queryKey: instanceTypeKeys.publicList(),
    queryFn: getPublicInstanceTypes,
    staleTime: instanceTypeStaleTimeMs,
  };
}

type InstanceTypeQueryKey = ReturnType<typeof instanceTypeKeys.detail>;

export function instanceTypeQueryOptions(
  instanceType: string,
  cloudCredentialId: string,
): UseQueryOptions<
  GPUInstanceType | null,
  Error,
  GPUInstanceType | null,
  InstanceTypeQueryKey
> {
  return {
    queryKey: instanceTypeKeys.detail(instanceType, cloudCredentialId),
    queryFn: ({ signal }: QueryFunctionContext<InstanceTypeQueryKey>) =>
      getInstanceTypeForCloudCredential(instanceType, cloudCredentialId, {
        signal,
      }),
    enabled: Boolean(instanceType && cloudCredentialId),
    staleTime: instanceTypeStaleTimeMs,
  };
}

export function useInstanceTypeQuery(
  instanceType: string,
  cloudCredentialId: string,
) {
  return useQuery(instanceTypeQueryOptions(instanceType, cloudCredentialId));
}
